<template>
  <div>
    <v-toolbar flat color="white">
      <v-toolbar-title>Anlaşmalı Bankalar</v-toolbar-title>
      <v-spacer></v-spacer>
      <v-btn color="primary" :to="{ path: '/admin/partner-bank/detail' }">
        <v-icon left>add</v-icon>
        Yeni Banka Ekle
      </v-btn>
    </v-toolbar>

    <v-card>
      <v-card-text>
        <v-layout row wrap>
          <v-flex xs12 md4>
            <v-text-field
              v-model="search"
              append-icon="search"
              label="Banka Adı veya IBAN Belirteci ile Ara"
              single-line
              hide-details
              @input="searchBanks"
              clearable
            ></v-text-field>
          </v-flex>
          <v-flex xs12 md3 pl-2>
            <v-select
              v-model="tenantFilter"
              :items="tenantFilterOptions"
              label="Tenant Filtrele"
              single-line
              hide-details
              @change="searchBanks"
              clearable
            ></v-select>
          </v-flex>
          <v-flex xs12 md3 pl-2>
            <v-select
              v-model="statusFilter"
              :items="statusOptions"
              label="Durum Filtrele"
              single-line
              hide-details
              @change="searchBanks"
              clearable
            ></v-select>
          </v-flex>
        </v-layout>

        <v-data-table
          :headers="headers"
          :items="partnerBanks"
          :loading="loading"
          class="elevation-1 mt-3"
          :rows-per-page-items="[10, 15, 25, 50]"
        >
          <template slot="items" slot-scope="props">
            <td> {{ getTenantName(props.item.tenantId) }}</td>
            <td>
              <span class="font-weight-bold">{{ props.item.ibanIdentifier }}</span>
            </td>
            <td>{{ props.item.bankName }}</td>
            <td>
              <v-chip 
                v-if="props.item.accountPurityType"
                small 
                :color="getPurityColor(props.item.accountPurityType)"
                text-color="white"
              >
                {{ props.item.accountPurityType }} Milyem
              </v-chip>
              <span v-else class="grey--text">-</span>
            </td>
            <td>
              <span v-if="props.item.goldAmountInGrams" class="font-weight-medium">
                {{ formatGoldAmount(props.item.goldAmountInGrams) }}
                <v-icon small color="amber darken-2">grain</v-icon>
              </span>
              <span v-else class="grey--text">-</span>
            </td>
            <td>
              <span v-if="props.item.cashAmount !== null && props.item.cashAmount !== undefined" class="font-weight-medium">
                {{ formatCashAmount(props.item.cashAmount) }}
              </span>
              <span v-else class="grey--text">-</span>
            </td>
            <td>
              <v-chip
                small
                :color="props.item.status === 'ACTIVE' ? 'success' : 'error'"
                text-color="white"
              >
                {{ props.item.status === 'ACTIVE' ? 'Aktif' : 'Pasif' }}
              </v-chip>
            </td>
            <td>{{ formatDate(props.item.createdDate) }}</td>
            <td class="text-xs-center">
              <v-btn
                icon
                small
                color="primary"
                :to="{ path: `/admin/partner-bank/detail/${props.item.id}` }"
                title="Düzenle"
              >
                <v-icon small>edit</v-icon>
              </v-btn>
              <v-btn
                icon
                small
                color="error"
                @click="confirmDelete(props.item)"
                title="Sil"
              >
                <v-icon small>delete</v-icon>
              </v-btn>
            </td>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="deleteDialog" max-width="400">
      <v-card>
        <v-card-title class="headline">Silme Onayı</v-card-title>
        <v-card-text>
          <strong>{{ bankToDelete && bankToDelete.bankName }}</strong> bankasını silmek istediğinizden emin misiniz?
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" flat @click="deleteDialog = false">İptal</v-btn>
          <v-btn color="error" @click="deleteBank" :loading="deleting">Sil</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import moment from 'moment';

export default {
  data() {
    return {
      search: '',
      statusFilter: '',
      tenantFilter: '',
      tenantFilterOptions: [
        { text: 'Tümü', value: '' },
        { text: 'ALTINDAFIRSAT', value: 1 },
        { text: 'E-ALTIN', value: 2 }
      ],
      loading: false,
      deleteDialog: false,
      deleting: false,
      bankToDelete: null,
      statusOptions: [
        { text: 'Tümü', value: '' },
        { text: 'Aktif', value: 'ACTIVE' },
        { text: 'Pasif', value: 'PASSIVE' }
      ],
      headers: [
        { text: 'Tenant', value: 'tenantId', sortable: true },
        { text: 'IBAN Belirteci', value: 'ibanIdentifier', sortable: true },
        { text: 'Banka Adı', value: 'bankName', sortable: true },
        { text: 'Hesap Milyem Türü', value: 'accountPurityType', sortable: true },
        { text: 'Altın Miktarı (Gram)', value: 'goldAmountInGrams', sortable: true },
        { text: 'Nakit Tutarı', value: 'cashAmount', sortable: true },
        { text: 'Durum', value: 'status', sortable: true },
        { text: 'Oluşturma Tarihi', value: 'createdDate', sortable: true },
        { text: 'İşlemler', value: 'actions', sortable: false, align: 'center' }
      ]
    };
  },

  computed: {
    partnerBanks() {
      return this.$store.state.partnerBanks.all || [];
    }
  },

  methods: {
    async loadBanks() {
      this.loading = true;
      try {
        await this.$store.dispatch('partnerBanks/searchPartnerBanks', {
          search: this.search,
          status: this.statusFilter,
          tenantId: this.tenantFilter || null
        });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Bankalar yüklenirken hata:', error);
      } finally {
        this.loading = false;
      }
    },

    searchBanks() {
      this.loadBanks();
    },

    confirmDelete(bank) {
      this.bankToDelete = bank;
      this.deleteDialog = true;
    },

    async deleteBank() {
      if (!this.bankToDelete) return;
      
      this.deleting = true;
      try {
        await this.$store.dispatch('partnerBanks/deletePartnerBank', this.bankToDelete);
        this.deleteDialog = false;
        this.bankToDelete = null;
        this.$store.state.message = 'Banka başarıyla silindi!';
        this.$store.state.dialog = true;
      } catch (error) {
        alert('Silme işlemi sırasında hata oluştu!');
      } finally {
        this.deleting = false;
      }
    },

    formatDate(date) {
      return date ? moment(date).format('DD.MM.YYYY HH:mm') : '-';
    },

    formatGoldAmount(amount) {
      if (!amount) return '-';
      return new Intl.NumberFormat('tr-TR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(amount);
    },

    formatCashAmount(amount) {
      if (amount === null || amount === undefined) return '-';
      return new Intl.NumberFormat('tr-TR', {
        style: 'currency',
        currency: 'TRY',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(amount);
    },

    getPurityColor(purity) {
      switch(purity) {
        case 1000: return 'yellow darken-1';
        case 995: return 'amber darken-1';
        case 916: return 'deep-orange';
        default: return 'grey';
      }
    },

    getTenantName(tenantId) {
      switch(tenantId) {
        case 1: return 'ALTINDAFIRSAT';
        case 2: return 'E-ALTIN';
        default: return 'Bilinmiyor';
      }
    },
  },

  mounted() {
    this.loadBanks();
  }
};
</script>

<style scoped>
.v-chip {
  font-weight: 600;
}
</style>